import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Phone, Lightbulb, Factory, MapPin, PenTool,
  Wrench, ShieldCheck, Zap, Hammer, CheckCircle2,
} from "lucide-react";
import Seo, { localBusinessSchema, faqSchema } from "@/components/Seo";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { Btn, Overline } from "@/components/Btn";
import { Marquee } from "@/components/Marquee";
import { CtaBand } from "@/components/CtaBand";
import { COMPANY, SECTORS, WHY } from "@/data/company";
import { SERVICES } from "@/data/services";
import { ZONES } from "@/data/locales";
import { REALISATIONS } from "@/data/realisations";
import { IMAGES } from "@/data/images";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const HERO_LINES = ["Enseignes lumineuses", "& communication", "visuelle"];

const homeFaq = [
  { q: "Fabriquez-vous vous-mêmes les enseignes ?", a: "Oui. Nous sommes enseigniste fabricant : nous concevons, usinons l'aluminium, l'inox et le plexiglas dans notre atelier, puis nous posons nous-mêmes. Un seul interlocuteur de l'étude à l'installation." },
  { q: "Dans quelles zones intervenez-vous ?", a: "Dans l'Ain, le Pays de Gex, la Haute-Savoie, la région lyonnaise (Auvergne-Rhône-Alpes) et en Suisse romande : Genève, Nyon, Lausanne et le canton de Vaud." },
  { q: "Proposez-vous un devis gratuit ?", a: "Oui, l'étude et le devis sont gratuits et sans engagement. Nous réalisons un relevé de votre façade avant de chiffrer précisément votre projet." },
  { q: "Gérez-vous aussi la pose et la maintenance ?", a: "Oui, nos équipes posent sur nacelle, réalisent le raccordement électrique aux normes et assurent la maintenance et le dépannage des enseignes." },
];

const enseignes = SERVICES.filter((s) => s.category === "Enseignes").slice(0, 6);
const comViz = SERVICES.filter((s) => s.category === "Communication visuelle").slice(0, 6);

const SECTOR_PAGE = {
  commerces: "enseigne-commerce",
  restaurants: "enseigne-restaurant",
  hotels: "enseigne-hotel",
  pharmacies: "enseigne-pharmacie",
  concessions: "enseigne-concession-automobile",
  garages: "enseigne-garage",
};

const steps = [
  { icon: PenTool, title: "Conception", desc: "Étude graphique, relevé de façade et faisabilité technique." },
  { icon: Factory, title: "Fabrication", desc: "Usinage aluminium, inox et plexiglas, montage LED en atelier." },
  { icon: Wrench, title: "Installation", desc: "Pose sur nacelle, raccordement aux normes et maintenance." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.72]);

  return (
    <>
      <Seo
        title="Ronalp Enseigne | Enseigne Lumineuse & Communication Visuelle"
        description="Fabrication et pose d'enseignes lumineuses, lettres relief, signalétique et communication visuelle. Ain, Haute-Savoie, Lyon, Genève et Vaud. Demandez votre devis."
        path="/"
        image={IMAGES.storefrontNight}
        jsonLd={[localBusinessSchema(), faqSchema(homeFaq)]}
      />

      {/* ---------------- HERO ---------------- */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-end overflow-hidden bg-ink-900" data-testid="hero">
        <motion.div style={{ y: imgY }} className="absolute inset-0 z-0 scale-110">
          <img src={IMAGES.storefrontNight} alt="Enseigne lumineuse de nuit sur une façade commerciale" className="h-full w-full object-cover" />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-[1] bg-ink-900" />
        <div className="absolute inset-0 z-[2] bg-gradient-to-t from-ink-900 via-ink-900/50 to-ink-900/20" />
        <div className="absolute inset-0 z-[3] bg-grid bg-grid-fade opacity-40" />

        <div className="relative z-10 mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 pb-16 lg:pb-24 pt-28">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Overline>Conception • Fabrication • Installation</Overline>
          </motion.div>

          <h1 className="mt-6 font-heading font-extrabold uppercase tracking-tight leading-[0.95] text-white text-4xl sm:text-6xl lg:text-7xl max-w-4xl">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="reveal-line">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.85, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 2 ? <span className="chrome-text">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-7 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed"
          >
            Ronalp Enseigne accompagne les professionnels dans la création d'enseignes lumineuses,
            de signalétique et de solutions de communication visuelle sur mesure, en France et en Suisse.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-9 flex flex-col sm:flex-row gap-3"
          >
            <Btn to="/contact/" size="lg" data-testid="hero-devis-btn">Demander un devis <ArrowRight className="h-4 w-4" /></Btn>
            <Btn to="/realisations/" variant="outline" size="lg" data-testid="hero-realisations-btn">Voir nos réalisations</Btn>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.8 }}
            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs font-mono uppercase tracking-wider text-chrome-mid"
          >
            {["Enseigniste fabricant", "Pose sur nacelle", "LED basse consommation", "France & Suisse"].map((t) => (
              <span key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-signature" /> {t}</span>
            ))}
          </motion.div>
        </div>
      </section>

      <Marquee items={["Enseigne lumineuse", "Lettres boîtiers", "Caisson lumineux", "Néon LED", "Signalétique", "Totem", "Vitrophanie", "Marquage véhicule", "Habillage façade"]} />

      {/* ---------------- NOS ENSEIGNES ---------------- */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="section-enseignes">
        <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
          <div>
            <Overline>Nos enseignes</Overline>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white">Des enseignes lumineuses fabriquées sur mesure</h2>
          </div>
          <Link to="/enseigne-lumineuse/" className="text-sm font-medium text-chrome-light hover:text-signature flex items-center gap-1 shrink-0">
            Toutes les enseignes <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>

        <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {enseignes.map((s) => (
            <StaggerItem key={s.slug}>
              <Link to={`/${s.slug}/`} className="group block h-full relative overflow-hidden rounded-xl border border-white/10 bg-ink-800 hover:border-signature/40 transition-all duration-300" data-testid={`enseigne-card-${s.slug}`}>
                <div className="relative h-44 overflow-hidden">
                  <img src={s.image} alt={s.h1} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-800 via-ink-800/30 to-transparent" />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-white group-hover:text-signature transition-colors">{s.nav}</h3>
                  <p className="mt-2 text-sm text-slate-400 line-clamp-2">{s.intro[0]}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-chrome-mid group-hover:text-signature transition-colors">
                    Découvrir <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* ---------------- COMMUNICATION VISUELLE (bento) ---------------- */}
      <section className="relative border-y border-white/10 bg-ink-800 noise-overlay" data-testid="section-comviz">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal className="mb-12 max-w-2xl">
            <Overline>Communication visuelle</Overline>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white">Une image cohérente, du trottoir au véhicule</h2>
            <p className="mt-4 text-slate-300">Vitrophanie, adhésifs, marquage de véhicules, totems, habillage de façade et impression grand format : nous produisons et posons tous vos supports.</p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <Reveal className="lg:col-span-2 lg:row-span-2">
              <Link to="/communication-visuelle/" className="group relative block h-full min-h-[320px] overflow-hidden rounded-xl border border-white/10" data-testid="comviz-feature">
                <img src={IMAGES.storefront2} alt="Communication visuelle et signalétique" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-transparent" />
                <div className="relative flex h-full flex-col justify-end p-8">
                  <h3 className="text-2xl font-bold text-white">Vue d'ensemble</h3>
                  <p className="mt-2 text-slate-300 max-w-md">Enseignes, signalétique, adhésifs et grand format : tous vos supports gérés en interne.</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm text-signature">Explorer <ArrowRight className="h-4 w-4" /></span>
                </div>
              </Link>
            </Reveal>
            {comViz.slice(0, 4).map((s) => (
              <Reveal key={s.slug}>
                <Link to={`/${s.slug}/`} className="group flex h-full min-h-[150px] flex-col justify-between rounded-xl border border-white/10 bg-ink-700 p-6 hover:border-signature/40 hover:bg-ink-600 transition-all" data-testid={`comviz-card-${s.slug}`}>
                  <Zap className="h-6 w-6 text-signature" />
                  <div>
                    <h3 className="text-base font-semibold text-white group-hover:text-signature transition-colors">{s.nav}</h3>
                    <ArrowUpRight className="mt-1 h-4 w-4 text-chrome-mid group-hover:text-signature transition-colors" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- CONCEPTION & FABRICATION ---------------- */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="section-fabrication">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-3 rounded-2xl bg-gradient-to-tr from-signature/20 to-electric/10 blur-2xl" />
              <img src={IMAGES.workshopWelding} alt="Fabrication d'enseigne en atelier : découpe et soudure du métal" className="relative rounded-2xl border border-white/10 w-full h-[420px] object-cover" loading="lazy" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>Conception &amp; fabrication</Overline>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white">Un savoir-faire d'atelier, pas seulement graphique</h2>
            <p className="mt-4 text-slate-300">Nous maîtrisons physiquement la fabrication : découpe et pliage de l'aluminium, travail de l'inox et du plexiglas, câblage LED et assemblage. C'est cette expertise qui garantit des enseignes durables et parfaitement éclairées.</p>
            <div className="mt-8 space-y-4">
              {steps.map((st, i) => (
                <div key={st.title} className="flex items-start gap-4 rounded-xl border border-white/10 bg-ink-800 p-5">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-signature/15 text-signature">
                    <st.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-chrome-dark">0{i + 1}</span>
                      <h3 className="text-base font-semibold text-white">{st.title}</h3>
                    </div>
                    <p className="mt-1 text-sm text-slate-400">{st.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- RÉALISATIONS ---------------- */}
      <section className="relative border-y border-white/10 bg-ink-800" data-testid="section-realisations">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
            <div>
              <Overline>Nos dernières réalisations</Overline>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white">Des projets menés de A à Z</h2>
            </div>
            <Link to="/realisations/" className="text-sm font-medium text-chrome-light hover:text-signature flex items-center gap-1 shrink-0">
              Toutes les réalisations <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {REALISATIONS.slice(0, 3).map((r) => (
              <StaggerItem key={r.slug}>
                <Link to={`/realisations/${r.slug}/`} className="group block overflow-hidden rounded-xl border border-white/10 bg-ink-700 hover:shadow-2xl hover:shadow-signature/10 transition-all" data-testid={`realisation-card-${r.slug}`}>
                  <div className="relative h-56 overflow-hidden">
                    <img src={r.cover} alt={r.alt} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-900/90 to-transparent" />
                    <span className="absolute bottom-4 left-4 rounded-full bg-ink-900/80 px-3 py-1 text-xs font-mono uppercase tracking-wider text-signature border border-signature/30">{r.city}</span>
                  </div>
                  <div className="p-6">
                    <p className="text-xs font-mono uppercase tracking-wider text-chrome-mid">{r.type}</p>
                    <h3 className="mt-2 text-lg font-semibold text-white group-hover:text-signature transition-colors line-clamp-2">{r.title}</h3>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ---------------- POURQUOI ---------------- */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="section-why">
        <Reveal className="mb-12 max-w-2xl">
          <Overline>Pourquoi Ronalp Enseigne ?</Overline>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white">Un partenaire industriel de confiance</h2>
        </Reveal>
        <Stagger className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {WHY.map((w, i) => {
            const icons = [Hammer, Lightbulb, MapPin, ShieldCheck];
            const Icon = icons[i];
            return (
              <StaggerItem key={w.title}>
                <div className="flex h-full items-start gap-5 rounded-xl border border-white/10 bg-ink-800 p-7 hover:border-white/20 transition-colors">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-signature/20 to-electric/10 text-signature">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{w.title}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{w.desc}</p>
                  </div>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>

      {/* ---------------- SECTEURS ---------------- */}
      <section className="relative border-y border-white/10 bg-ink-800 noise-overlay" data-testid="section-secteurs">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal className="mb-12 max-w-2xl flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <Overline>Nos secteurs d'activité</Overline>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white">Des enseignes pour chaque métier</h2>
            </div>
            <Link to="/secteurs/" className="text-sm font-medium text-chrome-light hover:text-signature flex items-center gap-1 shrink-0">
              Tous les secteurs <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Stagger className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {SECTORS.map((s) => {
              const sectorSlug = SECTOR_PAGE[s.key];
              const inner = (
                <>
                  <h3 className="text-sm font-semibold text-white group-hover:text-signature transition-colors">{s.label}</h3>
                  <p className="mt-2 text-xs text-slate-400 leading-relaxed">{s.desc}</p>
                  {sectorSlug && <span className="mt-3 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-chrome-mid group-hover:text-signature transition-colors">Voir <ArrowUpRight className="h-3 w-3" /></span>}
                </>
              );
              return (
                <StaggerItem key={s.key}>
                  {sectorSlug ? (
                    <Link to={`/${sectorSlug}/`} className="group flex flex-col rounded-xl border border-white/10 bg-ink-700 p-5 hover:border-signature/40 transition-colors h-full" data-testid={`secteur-${s.key}`}>
                      {inner}
                    </Link>
                  ) : (
                    <div className="group rounded-xl border border-white/10 bg-ink-700 p-5 hover:border-signature/40 transition-colors h-full" data-testid={`secteur-${s.key}`}>
                      {inner}
                    </div>
                  )}
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* ---------------- ZONES ---------------- */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="section-zones">
        <Reveal className="mb-12 max-w-2xl">
          <Overline>Nos zones d'intervention</Overline>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white">France &amp; Suisse</h2>
          <p className="mt-4 text-slate-300">Un ancrage fort sur le bassin lémanique et le Pays de Gex, étendu à Lyon, l'Ain, la Haute-Savoie et la Suisse romande.</p>
        </Reveal>
        <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {ZONES.map((z) => (
            <StaggerItem key={z.key}>
              <Link to={`/${z.hub}/`} className="group flex items-center justify-between rounded-xl border border-white/10 bg-ink-800 p-7 hover:border-signature/40 hover:bg-ink-700 transition-all" data-testid={`zone-${z.key}`}>
                <div className="flex items-center gap-4">
                  <MapPin className="h-5 w-5 text-signature" />
                  <div>
                    <h3 className="text-lg font-semibold text-white group-hover:text-signature transition-colors">{z.label}</h3>
                    <p className="text-xs font-mono uppercase tracking-wider text-chrome-dark">{z.country}</p>
                  </div>
                </div>
                <ArrowUpRight className="h-5 w-5 text-chrome-mid group-hover:text-signature transition-colors" />
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* ---------------- FAQ ---------------- */}
      <section className="relative border-t border-white/10 bg-ink-800" data-testid="section-faq">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal className="mb-10 text-center">
            <Overline className="justify-center">FAQ</Overline>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white">Questions fréquentes</h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="space-y-3">
              {homeFaq.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="rounded-xl border border-white/10 bg-ink-700 px-5" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left text-white hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-400 pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
